<template>
	<BaseFundCard
		class="fund-card-callout"
		:fundData="fundData"
		v-slot="{
			nameTruncated,
			fundStats
		}"
	>
		<div class="fund-card" :class="sizeClasses" :title="titleAttribute">
			<FundCardHeader :fundData="fundData" :name="nameTruncated" />

			<FundCardStats :fundStats="fundStats">
				<template #customStatLast>
					<div
						v-if="morningstar"
						class="col sm-auto fund-card-stat pdp-stat-box-override"
					>
						<Morningstar
							:content="morningstar.content"
							:fundData="morningstar.fundData"
						/>
					</div>
				</template>
			</FundCardStats>
		</div>
	</BaseFundCard>
</template>

<script>
import BaseFundCard from "./base/BaseFundCard";
import { FundCardHeader, FundCardStats } from "./components";
import Morningstar from "../morningstar/Morningstar";

/**
 * This component makes use of a base component ([`base/BaseFundCard.vue`](./base/README.md)) which handles global calculations and styles, and the sub-components `components/FundCardHeader.vue` and `components/FundCardStats.vue`. Data is passed between the components via props and scoped slots. The Morningstar component is also used.
 */
export default {
	name: "FundCardCallout",

	components: {
		BaseFundCard,
		FundCardHeader,
		FundCardStats,
		Morningstar
	},

	props: {
		/**
		 * An object containing the fund data for the card callout. Possible object properties are: `{ fundId, name, titleAttribute, description, symbol, url, stats [{ label, value, caption }]}`.
		 */
		fundData: {
			type: Object
		},

		/**
		 * An optional string of classes.
		 */
		sizeClasses: {
			type: String,
			default: ""
		},

		/**
		 * An object containing the Morningstar data. This object contains two nested objects: `{ content: {...}, fundData: {...} }` which align with the Morningstar Component props. Refer to the Morninstar component for object details.
		 */
		morningstar: {
			type: Object
		}
	},

	computed: {
		titleAttribute() {
			return this.fundData.titleAttribute
				? this.fundData.titleAttribute
				: this.fundData.name;
		}
	}
};
</script>

<style lang="scss">
$customMobileBp: 400px;
$customLargeBp: 1170px;

.fund-card-callout {
	.fund-card {
		&-header {
			padding: 32px 18px 24px;
			min-height: auto;
		}

		&-title {
			margin-bottom: 18px;
		}

		&-description {
			color: $color--almost-black;
			margin-bottom: 0;

			.footnote-link {
				line-height: 0;
			}
		}

		&-stats {
			padding: 0 18px 8px;
		}

		&-stat {
			margin-bottom: 0;
			padding-top: 32px;
			padding-right: 0;
			min-width: initial;
			width: 50%;

			@media screen and (max-width: $customMobileBp) {
				flex: 1 1 auto;
				width: auto;
				&:nth-child(even) {
					width: 130px;
				}
			}

			@media screen and (min-width: $customLargeBp) {
				width: auto;
			}
		}
	}

	.caption {
		margin-bottom: 0;
	}
}

.pdp-stat-box-override {
	.pdp-stat-box {
		padding: 0;
		display: inline-block;
		align-content: initial;
		flex-wrap: initial;
		justify-content: initial;
		border-bottom: 0;

		span[data-tooltip-trigger] {
			line-height: 1;
		}
	}
}
</style>
